import { useFieldArray, useFormContext } from "react-hook-form";
import { TrashIcon, PlusIcon } from "lucide-react";
import MetafieldForm from "@/pages/(app)/customer-portal/components/customer-portal-basics/metafield-form.tsx";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Button } from "@babackapp/ui/components/button";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormTagsInput from "@babackapp/react-hook-forms-components/form-tags-input";
import { Switch } from "@babackapp/ui/components/switch";
import useTranslation from "@babackapp/translations/use-translation";

export const CustomerSegmentsForm = () => {
  const t = useTranslation();
  const formContext = useFormContext();

  const customerSegments = useFieldArray({
    control: formContext.control,
    name: "customerSegments",
  });

  const removeSegment = (index: number) => {
    customerSegments.remove(index);
  };

  const addSegment = () => {
    customerSegments.append({
      name: "",
      identificationMetafields: [],
    });
  };

  return (
    <div className={"space-y-4"}>
      <div>
        <FormLabel>{t("settings.config.customerSegments.title")}</FormLabel>
        <FormDescription>
          {t("settings.config.customerSegments.description")}
        </FormDescription>
      </div>
      <div className={"space-y-2"}>
        {customerSegments.fields.map((_, index) => {
          return (
            <Card key={index} className={"relative"}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-2"
                onClick={() => removeSegment(index)}
              >
                <TrashIcon className="size-4" />
              </Button>
              <CardHeader>
                <CardTitle>Segment {index + 1}</CardTitle>
              </CardHeader>
              <CardContent className={"space-y-2"}>
                <FormField
                  control={formContext.control}
                  name={`customerSegments.${index}.name`}
                  render={({ field }) => {
                    return (
                      <FormInput
                        label={t(
                          "settings.config.customerSegments.inputs.name.label",
                        )}
                        {...field}
                      />
                    );
                  }}
                />
                <FormField
                  name={`customerSegments.${index}.identificationMetafields`}
                  render={() => (
                    <MetafieldForm
                      name={`customerSegments.${index}.identificationMetafields`}
                      label={t(
                        "settings.config.customerSegments.inputs.metafields.label",
                      )}
                      description={t(
                        "settings.config.customerSegments.inputs.metafields.description",
                      )}
                    />
                  )}
                />
                <FormField
                  control={formContext.control}
                  name={`customerSegments.${index}.identificationTags`}
                  render={({ field }) => (
                    <FormTagsInput
                      label={t(
                        "settings.config.customerSegments.inputs.tags.label",
                      )}
                      description={t(
                        "settings.config.customerSegments.inputs.tags.description",
                      )}
                      {...field}
                    />
                  )}
                />

                <div className={"space-y-2"}>
                  <FormLabel>Options</FormLabel>
                  <FormField
                    control={formContext.control}
                    name={`customerSegments.${index}.hasFreeReturn`}
                    render={({ field }) => {
                      return (
                        <FormItem
                          className={
                            "flex flex-row items-center space-x-3 space-y-0"
                          }
                        >
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel>
                            {t(
                              "settings.config.customerSegments.inputs.hasFreeReturn.label",
                            )}
                          </FormLabel>
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Button
        type="button"
        className="flex gap-2 justify-self-start"
        size="sm"
        variant="outline"
        onClick={addSegment}
      >
        {t("settings.config.customerSegments.inputs.addSegment")}
        <PlusIcon className="size-4" />
      </Button>
    </div>
  );
};
